<template>
  <v-dialog
    v-model="localVisible"
    max-width="500"
    persistent
    @after-enter="focusEmailField"
  >
    <v-card>
      <v-card-title class="invite-modal__header d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon icon="mdi-account-plus" class="mr-2" />
          <span>Invite Friends</span>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :disabled="sending"
          @click="closeModal"
        />
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-6">
        <!-- Success State -->
        <div v-if="successMessage" class="text-center py-4">
          <v-icon icon="mdi-check-circle" color="success" size="64" class="mb-4" />
          <p class="text-body-1">{{ successMessage }}</p>
          <v-btn color="primary" variant="tonal" class="mt-4" @click="resetForm">
            Send Another Invitation
          </v-btn>
        </div>

        <!-- Form State -->
        <v-form v-else ref="formRef" @submit.prevent="sendInvitation">
          <p class="text-body-2 mb-4" style="opacity: 0.7">
            Share the experience with your friends! Enter their email address below and we'll send them an invitation to join.
          </p>

          <!-- Single Email Input -->
          <v-text-field
            v-if="!multipleMode"
            ref="emailFieldRef"
            v-model="email"
            label="Friend's Email Address"
            type="email"
            :rules="emailRules"
            :error-messages="errorMessage"
            :disabled="sending"
            variant="outlined"
            prepend-inner-icon="mdi-email"
            placeholder="friend@example.com"
            @keyup.enter="sendInvitation"
          />

          <!-- Multiple Emails Toggle -->
          <v-switch
            v-model="multipleMode"
            label="Send to multiple friends"
            class="mt-2"
            :disabled="sending"
            color="primary"
            hide-details
          />

          <!-- Multiple Emails Input -->
          <v-textarea
            v-if="multipleMode"
            v-model="multipleEmails"
            label="Email Addresses (one per line)"
            :rules="multipleEmailsRules"
            :disabled="sending"
            variant="outlined"
            rows="4"
            placeholder="friend1@example.com&#10;friend2@example.com&#10;friend3@example.com"
            class="mt-4"
          />

          <!-- Invalid Emails Warning -->
          <v-alert
            v-if="invalidEmails.length > 0"
            type="warning"
            variant="tonal"
            class="mt-4"
            density="compact"
          >
            <strong>Invalid emails:</strong> {{ invalidEmails.join(', ') }}
          </v-alert>
        </v-form>
      </v-card-text>

      <v-divider v-if="!successMessage" />

      <v-card-actions v-if="!successMessage" class="pa-4">
        <v-spacer />
        <v-btn variant="text" :disabled="sending" @click="closeModal">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="sending"
          :disabled="!canSend"
          @click="sendInvitation"
        >
          <v-icon icon="mdi-send" class="mr-1" />
          {{ multipleMode ? 'Send Invitations' : 'Send Invitation' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useSdk } from '@/composables/useSdk';

interface Props {
  modelValue: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'invited', email: string | string[]): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { sdk } = useSdk();

// Local state

// Only an explicit X / Cancel (closeModal) may close the dialog. Vuetify's
// `persistent` does not hold on iOS: a delayed synthetic scrim click (fired
// after the keyboard opens) reaches the overlay and writes modelValue=false
// shortly after open. This flag rejects every close we didn't explicitly ask for.
const allowClose = ref(false);

const localVisible = computed({
  get: () => props.modelValue,
  set: (value) => {
    if (!value && !allowClose.value) return;
    emit('update:modelValue', value);
    if (!value) allowClose.value = false;
  },
});

const formRef = ref<HTMLFormElement | null>(null);
const emailFieldRef = ref<{ $el: HTMLElement } | null>(null);
const email = ref('');
const multipleEmails = ref('');
const multipleMode = ref(false);
const sending = ref(false);
const successMessage = ref('');
const errorMessage = ref('');
const invalidEmails = ref<string[]>([]);

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Validation rules
const emailRules = [
  (v: string) => !multipleMode.value || !!v || 'Email is required',
  (v: string) => !v || emailRegex.test(v) || 'Please enter a valid email',
];

const multipleEmailsRules = [
  (v: string) => !multipleMode.value || !!v.trim() || 'At least one email is required',
];

// Computed
const canSend = computed(() => {
  if (sending.value) return false;

  if (multipleMode.value) {
    const emails = multipleEmails.value.split('\n').filter((e) => e.trim());
    return emails.length > 0;
  }

  return email.value.trim() && emailRegex.test(email.value.trim());
});

// Methods

/**
 * Focus the email input once the dialog's open transition is done.
 *
 * iOS Safari/WKWebView drops the keyboard when an input is focused mid-transition
 * (the old `autofocus` race): the field shows a caret, then deactivates and the
 * keyboard never sticks. Focusing in `@after-enter` — after layout settles, on the
 * next paint via double rAF — is the timing iOS accepts. Mirrors the
 * `ImageCropDialog` pattern.
 */
function focusEmailField() {
  if (multipleMode.value || successMessage.value) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const input = emailFieldRef.value?.$el?.querySelector('input');
      input?.focus({ preventScroll: true });
    });
  });
}

function closeModal() {
  if (!sending.value) {
    allowClose.value = true;
    localVisible.value = false;
    resetForm();
  }
}

function resetForm() {
  email.value = '';
  multipleEmails.value = '';
  successMessage.value = '';
  errorMessage.value = '';
  invalidEmails.value = [];
}

function validateEmails(emails: string[]): {
  valid: string[];
  invalid: string[];
} {
  const valid: string[] = [];
  const invalid: string[] = [];

  emails.forEach((emailStr) => {
    const trimmed = emailStr.trim().toLowerCase();
    if (trimmed && emailRegex.test(trimmed)) {
      valid.push(trimmed);
    } else if (trimmed) {
      invalid.push(trimmed);
    }
  });

  return { valid, invalid };
}

async function sendInvitation() {
  if (!canSend.value) return;

  errorMessage.value = '';
  invalidEmails.value = [];
  sending.value = true;

  try {
    if (multipleMode.value) {
      // Multiple emails mode
      const emailList = multipleEmails.value
        .split('\n')
        .map((e) => e.trim())
        .filter((e) => e);

      const validation = validateEmails(emailList);

      if (validation.invalid.length > 0) {
        invalidEmails.value = validation.invalid;
      }

      if (validation.valid.length === 0) {
        errorMessage.value = 'No valid email addresses provided';
        return;
      }

      await sdk.inviteByEmailMultiple(validation.valid);
      successMessage.value = `Invitations sent to ${validation.valid.length} friend${validation.valid.length > 1 ? 's' : ''}!`;
      emit('invited', validation.valid);
    } else {
      // Single email mode
      const trimmedEmail = email.value.trim();
      await sdk.inviteByEmail(trimmedEmail);
      successMessage.value = `Invitation sent to ${trimmedEmail}!`;
      emit('invited', trimmedEmail);
    }
  } catch (error) {
    console.error('Failed to send invitation:', error);
    errorMessage.value = error instanceof Error ? error.message : 'Failed to send invitation';
  } finally {
    sending.value = false;
  }
}

// Watch for dialog close to reset
watch(localVisible, (visible) => {
  if (!visible) {
    // Small delay to allow animation
    setTimeout(resetForm, 300);
  }
});
</script>

<style scoped lang="scss">
.invite-modal__header {
  font-size: 1.1rem;
  font-weight: 600;
  padding-top: calc(16px + var(--safe-area-top));
}

.v-switch {
  :deep(.v-label) {
    font-size: 0.875rem;
  }
}
</style>
